import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { restaurantCategoriesService } from '../../../services/api';

const MOCK_CATEGORIES = [
    { id: 1, name: 'Restaurantes', icon: '🍽️', type: 'store' },
    { id: 2, name: 'Cafeterías', icon: '☕', type: 'store' },
    { id: 3, name: 'Heladerías', icon: '🍦', type: 'store' },
    { id: 4, name: 'Panaderías', icon: '🥐', type: 'store' },
    { id: 5, name: 'Licoreras', icon: '🍻', type: 'store' },
    { id: 6, name: 'Supermercados', icon: '🛒', type: 'store' },
    { id: 101, name: 'Comida Rápida', icon: '🍔', type: 'subcategory', parent_id: 1 },
    { id: 102, name: 'Pizzerías', icon: '🍕', type: 'subcategory', parent_id: 1 },
    { id: 103, name: 'Alitas', icon: '🍗', type: 'subcategory', parent_id: 1 },
    { id: 104, name: 'Desayunos', icon: '🍳', type: 'subcategory', parent_id: 1 },
    { id: 105, name: 'Mariscos', icon: '🦐', type: 'subcategory', parent_id: 1 },
    { id: 106, name: 'Asados', icon: '🥩', type: 'subcategory', parent_id: 1 },
    { id: 201, name: 'Café', icon: '☕', type: 'subcategory', parent_id: 2 },
    { id: 202, name: 'Té & Infusiones', icon: '🫖', type: 'subcategory', parent_id: 2 },
    { id: 203, name: 'Snacks', icon: '🥐', type: 'subcategory', parent_id: 2 },
    { id: 301, name: 'Helados', icon: '🍦', type: 'subcategory', parent_id: 3 },
    { id: 302, name: 'Batidos', icon: '🥤', type: 'subcategory', parent_id: 3 },
    { id: 303, name: 'Crepes', icon: '🫓', type: 'subcategory', parent_id: 3 },
    { id: 401, name: 'Pan Artesanal', icon: '🍞', type: 'subcategory', parent_id: 4 },
    { id: 402, name: 'Pasteles', icon: '🎂', type: 'subcategory', parent_id: 4 },
    { id: 403, name: 'Empanadas', icon: '🥟', type: 'subcategory', parent_id: 4 },
    { id: 501, name: 'Cervecerías', icon: '🍺', type: 'subcategory', parent_id: 5 },
    { id: 502, name: 'Vinos', icon: '🍷', type: 'subcategory', parent_id: 5 },
    { id: 503, name: 'Cócteles', icon: '🍹', type: 'subcategory', parent_id: 5 },
    { id: 601, name: 'Abarrotes', icon: '🏪', type: 'subcategory', parent_id: 6 },
    { id: 602, name: 'Frutas & Veg', icon: '🥦', type: 'subcategory', parent_id: 6 },
    { id: 603, name: 'Lácteos', icon: '🥛', type: 'subcategory', parent_id: 6 },
];

const DEFAULT_EMOJI = '🍽️';

const isUrl = (s) => typeof s === 'string' && (s.startsWith('http') || s.startsWith('/'));
const isSubcat = (item) => item.type === 'subcategory';

// Ojo: \p{Emoji} depende del soporte Unicode del runtime.
// Si te da problemas, puedo pasarte un fallback más compatible.
const stripEmoji = (str) => String(str || '').replace(/\p{Emoji}/gu, '').trim();

/* ─── Icons ─────────────────────────────────────────── */
const IconSearch = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);

const IconCheck = () => (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
        <polyline points="2,6 5,9 10,3" stroke="white" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

/* ─── Burst particles config ─────────────────────────── */
const BURST_PARTICLES = [
    { angle:   0, dist: 28, shape: '★', color: '#F97316' },
    { angle:  45, dist: 24, shape: '✦', color: '#FBBF24' },
    { angle:  90, dist: 28, shape: '★', color: '#10B981' },
    { angle: 135, dist: 24, shape: '✦', color: '#3B82F6' },
    { angle: 180, dist: 28, shape: '★', color: '#EC4899' },
    { angle: 225, dist: 24, shape: '✦', color: '#8B5CF6' },
    { angle: 270, dist: 28, shape: '★', color: '#F97316' },
    { angle: 315, dist: 24, shape: '✦', color: '#FBBF24' },
];

/* ─── Item ───────────────────────────────────────────── */
const CatItem = React.memo(function CatItem({ item, isSelected, onSelect }) {
    const isSub     = isSubcat(item);
    const emoji     = item.icon_emoji || (!isUrl(item.icon) ? item.icon : null) || DEFAULT_EMOJI;
    const showPhoto = !isSub && isUrl(item.icon);
    const name      = isSub ? stripEmoji(item.name) : item.name;
    const [burst, setBurst] = useState(false);
    const burstTimer = useRef(null);

    const handleSelect = () => {
        onSelect(item);
        setBurst(false);
        clearTimeout(burstTimer.current);
        // micro-delay para reiniciar la animación si ya estaba activa
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setBurst(true);
                burstTimer.current = setTimeout(() => setBurst(false), 600);
            });
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect();
        }
    };

    return (
        <div
            className={`ro-cat-item${isSelected ? ' ro-cat-item--sel' : ''}${showPhoto ? ' ro-cat-item--photo' : ''}`}
            onClick={handleSelect}
            role="button"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            aria-pressed={isSelected}
        >
            <div className={`ro-cat-circle${showPhoto ? ' ro-cat-circle--photo' : ''}`}>
                {/* Burst particles */}
                {burst && BURST_PARTICLES.map((p, i) => (
                    <span
                        key={i}
                        className="ro-cat-burst-dot"
                        aria-hidden
                        style={{
                            '--angle': `${p.angle}deg`,
                            '--dist':  `${p.dist}px`,
                            color:     p.color,
                            animationDelay: `${i * 18}ms`,
                        }}
                    >
                        {p.shape}
                    </span>
                ))}

                {showPhoto
                    ? <img src={item.icon} alt={item.name} className="ro-cat-circle-img" loading="lazy" />
                    : <span className="ro-cat-circle-emoji" aria-hidden>{emoji}</span>
                }
                <span className="ro-cat-circle-check" aria-hidden>
                    <IconCheck />
                </span>
            </div>
            <span className="ro-cat-label">{name}</span>
        </div>
    );
});

/* ─── Main step ──────────────────────────────────────── */
const StepRestaurantCategory = ({ formData, onUpdate, errors = {}, onClearError }) => {
    const [allCats, setAllCats] = useState(MOCK_CATEGORIES);
    const [loadingCats, setLoadingCats] = useState(false);
    const [search, setSearch] = useState('');
    const [loadError, setLoadError] = useState(null);

    // Estado derivado (evita desync)
    const isMain = !formData.category_id;

    useEffect(() => {
        let alive = true;

        const load = async () => {
            setLoadingCats(true);
            setLoadError(null);

            try {
                const res = await restaurantCategoriesService.getAll();
                const data = res?.data;

                if (alive && Array.isArray(data) && data.length) {
                    setAllCats(data);
                }
            } catch (e) {
                if (alive) setLoadError('No se pudieron cargar categorías. Usando valores por defecto.');
                // mantenemos mocks
            } finally {
                if (alive) setLoadingCats(false);
            }
        };

        load();
        return () => { alive = false; };
    }, []);

    const selectedMain = useMemo(() => {
        return allCats.find((c) => c.id === formData.category_id) || null;
    }, [allCats, formData.category_id]);

    const mainCats = useMemo(() => {
        return allCats.filter((c) => c.type === 'store' || c.type === 'category');
    }, [allCats]);

    const subCats = useMemo(() => {
        if (!formData.category_id) return [];
        return allCats.filter((c) => (
            c.type === 'subcategory' &&
            (c.parent_id == null || c.parent_id === formData.category_id)
        ));
    }, [allCats, formData.category_id]);

    const lc = useMemo(() => search.trim().toLowerCase(), [search]);

    const filteredMain = useMemo(() => {
        if (!lc) return mainCats;
        return mainCats.filter((c) => c.name.toLowerCase().includes(lc));
    }, [mainCats, lc]);

    const filteredSub = useMemo(() => {
        if (!lc) return subCats;
        return subCats.filter((c) => c.name.toLowerCase().includes(lc));
    }, [subCats, lc]);

    const clearErrors = useCallback((fields) => {
        if (!onClearError) return;
        fields.forEach((f) => onClearError(f));
    }, [onClearError]);

    const selectMain = useCallback((cat) => {
        onUpdate({ category_id: cat.id, subcategory_id: null });
        clearErrors(['category_id', 'subcategory_id']);
        setSearch('');
    }, [onUpdate, clearErrors]);

    const selectSub = useCallback((sub) => {
        onUpdate({ subcategory_id: sub.id });
        clearErrors(['subcategory_id']);
    }, [onUpdate, clearErrors]);

    const goBack = useCallback(() => {
        onUpdate({ category_id: null, subcategory_id: null });
        setSearch('');
        clearErrors(['category_id', 'subcategory_id']);
    }, [onUpdate, clearErrors]);

    return (
        <div className="ro-cat-step">
            <div className="ro-cat-step-hd">
                <span className="ro-eyebrow ro-eyebrow--accent">Paso 3 · Categoría</span>

                {isMain ? (
                    <>
                        <h2 className="ro-cat-step-title">¿Cómo describes tu negocio?</h2>
                        <p className="ro-cat-step-sub">Selecciona la categoría principal de tu negocio.</p>
                    </>
                ) : (
                    <>
                        <div className="ro-cat-breadcrumb">
                            <button type="button" className="ro-cat-back-pill" onClick={goBack}>
                                ← {selectedMain?.name}
                            </button>
                            <span className="ro-cat-breadcrumb-arr">›</span>
                            <span className="ro-cat-breadcrumb-cur">Especialidad</span>
                        </div>
                        <h2 className="ro-cat-step-title">¿Cuál es tu especialidad?</h2>
                        <p className="ro-cat-step-sub">Elige la subcategoría que mejor describe tu negocio.</p>
                    </>
                )}

                <div className="ro-neo-search">
                    <span className="ro-neo-search-ico"><IconSearch /></span>
                    <input
                        type="text"
                        className="ro-neo-search-input"
                        placeholder={isMain ? 'Buscar categoría...' : 'Buscar especialidad...'}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoComplete="off"
                        aria-label={isMain ? 'Buscar categoría' : 'Buscar especialidad'}
                    />
                    {search && (
                        <button
                            type="button"
                            className="ro-neo-search-clr"
                            onClick={() => setSearch('')}
                            aria-label="Limpiar búsqueda"
                        >
                            ✕
                        </button>
                    )}
                </div>

                {!!loadError && (
                    <div className="ro-neo-hint" role="status">{loadError}</div>
                )}
            </div>

            {loadingCats ? (
                <div className="ro-neo-loading"><span className="ro-spin" /> Cargando categorías...</div>
            ) : isMain ? (
                <div className="ro-cat-grid">
                    {filteredMain.length > 0 ? (
                        filteredMain.map((cat) => (
                            <CatItem
                                key={cat.id}
                                item={cat}
                                isSelected={formData.category_id === cat.id}
                                onSelect={selectMain}
                            />
                        ))
                    ) : (
                        <p className="ro-neo-empty">Sin resultados{search ? ` para "${search}"` : ''}</p>
                    )}
                </div>
            ) : subCats.length === 0 ? (
                <p className="ro-neo-empty">Sin especialidades para esta categoría.</p>
            ) : (
                <div className="ro-cat-grid">
                    {filteredSub.length > 0 ? (
                        filteredSub.map((sub) => (
                            <CatItem
                                key={sub.id}
                                item={sub}
                                isSelected={formData.subcategory_id === sub.id}
                                onSelect={selectSub}
                            />
                        ))
                    ) : (
                        <p className="ro-neo-empty">Sin resultados{search ? ` para "${search}"` : ''}</p>
                    )}
                </div>
            )}

            {isMain && errors.category_id && (
                <span className="ro-err-msg ro-neo-err">{errors.category_id}</span>
            )}
            {!isMain && errors.subcategory_id && (
                <span className="ro-err-msg ro-neo-err">{errors.subcategory_id}</span>
            )}

            {/* ── Barra de selección — subcategoría elegida ── */}
            {!isMain && (() => {
                const selSub = subCats.find((s) => s.id === formData.subcategory_id);
                const subEmoji = selSub
                    ? (selSub.icon_emoji || (!isUrl(selSub.icon) ? selSub.icon : null) || DEFAULT_EMOJI)
                    : null;
                return (
                    <div className={`ro-cat-cta${selSub ? ' ro-cat-cta--show' : ''}`}>
                        {selSub && (
                            <div className="ro-cat-cta-info">
                                <div className="ro-cat-cta-circle" aria-hidden>{subEmoji}</div>
                                <div className="ro-cat-cta-meta">
                                    <span className="ro-cat-cta-hint">Seleccionado</span>
                                    <span className="ro-cat-cta-name">{stripEmoji(selSub.name)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}
        </div>
    );
};

export default StepRestaurantCategory;